// src/components/Atelier3/Activite4/StakeholderRiskView.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Shield, TrendingDown, AlertTriangle, ChevronDown, ChevronUp, ArrowRight, Info, BarChart2, Settings, FileText } from 'lucide-react';
import api from '../../../api/apiClient';
import workshop3Service from '../../../services/workshop3Service';
import stakeholderService from '../../../services/stakeholderService';
import AiAssistantButton from '../../common/AiAssistantButton';
import { useAuth } from '../../../context/AuthContext';
import ThreatRadarVisualization from '../Activite1/ThreatRadarVisualization';

/**
 * Displays initial and residual risk per stakeholder, based on:
 * - Initial risk = stakeholder.threatLevel (calculated in Activité 1)
 * - Residual risk = threadLevel reduced by measures linked to the stakeholder's attack paths
 * - Displayed as one row per (stakeholder × measure) combination
 */
const StakeholderRiskView = ({ measures = [], analysisId }) => {
    const { t, i18n } = useTranslation();
    const { companyAiEnabled } = useAuth();

    const [stakeholders, setStakeholders] = useState([]);
    const [attackPaths, setAttackPaths] = useState([]);
    const [thresholds, setThresholds] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expandedStakeholders, setExpandedStakeholders] = useState({});
    const [showRadars, setShowRadars] = useState(true);
    const [isThresholdEditing, setIsThresholdEditing] = useState(false);
    const [expressionTexts, setExpressionTexts] = useState({});
    const [savingExpression, setSavingExpression] = useState({});
    const [editingExpressions, setEditingExpressions] = useState({});
    const [aiGenerating, setAiGenerating] = useState({});
    const [riskFilter, setRiskFilter] = useState('retained');

    const initialRadarRef = useRef(null);
    const residualRadarRef = useRef(null);

    const handleUpdateThresholds = async (newThresholds) => {
        try {
            const resp = await stakeholderService.updateThresholds(analysisId, newThresholds);
            if (resp.success) {
                setThresholds(newThresholds);
            }
        } catch (err) {
            console.error('[StakeholderRiskView] Error updating thresholds:', err);
        }
    };

    // ---------------------------------------------------------------------------
    // Data loading
    // ---------------------------------------------------------------------------
    useEffect(() => {
        if (analysisId) {
            loadData();
        }
    }, [analysisId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [stakeholderResp, pathsResp, thresholdsResp] = await Promise.all([
                api.get(`/analyses/${analysisId}/atelier3/stakeholders`),
                workshop3Service.getAttackPaths(analysisId),
                stakeholderService.getThresholds(analysisId)
            ]);

            if (stakeholderResp.success && stakeholderResp.data) {
                setStakeholders(stakeholderResp.data);
                const initialExpressions = {};
                stakeholderResp.data.forEach(s => {
                    if (s.residualRiskExpression) {
                        initialExpressions[s.id] = s.residualRiskExpression;
                    }
                });
                setExpressionTexts(initialExpressions);
            }
            if (pathsResp.success && pathsResp.data) {
                setAttackPaths(pathsResp.data);
            }
            if (thresholdsResp.success && thresholdsResp.data) {
                setThresholds(thresholdsResp.data);
            }
        } catch (err) {
            console.error('[StakeholderRiskView] Error loading data:', err);
        } finally {
            setLoading(false);
        }
    };

    // ---------------------------------------------------------------------------
    // Risk helpers
    // ---------------------------------------------------------------------------

    /**
     * Returns zone label and CSS classes based on threatLevel numeric value.
     * Mirrors the logic used in StakeholderTable.js / StakeholderForm.js
     */
    const getRiskZone = (threatLevel, thr) => {
        if (!thr) return { label: 'Hors périmètre', badge: 'bg-slate-100 text-slate-700 border border-slate-300', dot: 'bg-slate-400' };
        const { danger, control, watch } = thr;
        if (threatLevel >= danger) return { label: 'Zone de danger', badge: 'bg-red-100 text-red-800 border border-red-300', dot: 'bg-red-500' };
        if (threatLevel >= control) return { label: 'Zone de contrôle', badge: 'bg-orange-100 text-orange-800 border border-orange-300', dot: 'bg-orange-500' };
        if (threatLevel >= watch) return { label: 'Zone de veille', badge: 'bg-teal-100 text-teal-800 border border-teal-300', dot: 'bg-teal-500' };
        return { label: 'Hors périmètre', badge: 'bg-slate-100 text-slate-700 border border-slate-300', dot: 'bg-slate-400' };
    };

    const getImplementationFactor = (status) => {
        if (status === 'abandoned') return 0;
        return 1.0; // All statuses (planned, in-progress, implemented) treat as 100% effective per user request
    };

    const getAppliedEffectiveness = (measure) => {
        const factor = getImplementationFactor(measure.implementation);
        return {
            d: (measure.dependanceEffectiveness || 0) * factor,
            p: (measure.penetrationEffectiveness || 0) * factor,
            m: (measure.maturiteCyberEffectiveness || 0) * factor,
            c: (measure.confianceEffectiveness || 0) * factor
        };
    };

    const formatEffectivenessText = (eff) => {
        const parts = [];
        if (eff.d > 0) parts.push(`D-${eff.d.toFixed(1)}`);
        if (eff.p > 0) parts.push(`P-${eff.p.toFixed(1)}`);
        if (eff.m > 0) parts.push(`M+${eff.m.toFixed(1)}`);
        if (eff.c > 0) parts.push(`C+${eff.c.toFixed(1)}`);
        return parts.length > 0 ? parts.join(', ') : '-';
    };

    const getImplementationBadge = (status) => {
        switch (status) {
            case 'implemented':
                return 'bg-green-100 text-green-800 border border-green-200';
            case 'in-progress':
                return 'bg-blue-100 text-blue-800 border border-blue-200';
            case 'planned':
                return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
            default:
                return 'bg-gray-100 text-gray-700 border border-gray-200';
        }
    };

    const getImplementationLabel = (status) => {
        switch (status) {
            case 'implemented': return 'Implémentée';
            case 'in-progress': return 'En cours';
            case 'planned': return 'Planifiée';
            case 'abandoned': return 'Abandonnée';
            default: return status;
        }
    };

    const renderEffectivenessTags = (measure) => {
        const d = measure.dependanceEffectiveness || 0;
        const p = measure.penetrationEffectiveness || 0;
        const m = measure.maturiteCyberEffectiveness || 0;
        const c = measure.confianceEffectiveness || 0;
        
        return (
            <div className="flex flex-wrap gap-1 mt-1">
                {d > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-medium">D-{d}</span>}
                {p > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-medium">P-{p}</span>}
                {m > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-50 text-green-700 font-medium">M+{m}</span>}
                {c > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-50 text-green-700 font-medium">C+{c}</span>}
                {d === 0 && p === 0 && m === 0 && c === 0 && <span className="text-[10px] text-gray-400">Aucun impact</span>}
            </div>
        );
    };

    // ---------------------------------------------------------------------------
    // Build per-stakeholder risk rows
    // ---------------------------------------------------------------------------

    /**
     * For each retained stakeholder:
     *  1. Find all attack paths that reference this stakeholder
     *  2. Find all measures linked to those attack paths
     *  3. Compute total reduction and residual risk
     */
    const buildStakeholderRows = () => {
        // Only include retained stakeholders (the ones selected in Activité 1)
        const retainedStakeholders = stakeholders.filter(s => s.retained !== false);

        return retainedStakeholders
            .filter(stakeholder => riskFilter === 'all' || stakeholder.retained === true)
            .map(stakeholder => {
            const initialD = stakeholder.dependency || 1;
            const initialP = stakeholder.penetration || 1;
            const initialM = stakeholder.cyberMaturity || 1;
            const initialC = stakeholder.trust || 1;

            const initialRisk = (initialD * initialP) / (initialM * initialC);

            // Attack paths concerning this stakeholder
            const relatedPaths = attackPaths.filter(path => {
                if (!path.stakeholders || path.stakeholders.length === 0) return false;
                return path.stakeholders.some(s => {
                    const sId = typeof s === 'string' ? s : s.id;
                    return sId === stakeholder.id || s.name === stakeholder.name;
                });
            });

            const relatedPathIds = new Set(relatedPaths.map(p => p.id));

            // Measures linked to any of those attack paths
            const relatedMeasures = measures.filter(m => {
                const mPaths = m.attackPaths || (m.attackPathId ? [m.attackPathId] : []);
                return mPaths.some(pid => relatedPathIds.has(pid));
            });

            // Total reduction using new formula
            let currentD = initialD;
            let currentP = initialP;
            let currentM = initialM;
            let currentC = initialC;

            relatedMeasures.forEach(m => {
                const eff = getAppliedEffectiveness(m);
                currentD -= eff.d;
                currentP -= eff.p;
                currentM += eff.m;
                currentC += eff.c;
            });

            // Bound values between 1 and 4
            currentD = Math.max(1, Math.min(4, currentD));
            currentP = Math.max(1, Math.min(4, currentP));
            currentM = Math.max(1, Math.min(4, currentM));
            currentC = Math.max(1, Math.min(4, currentC));

            const residualRisk = (currentD * currentP) / (currentM * currentC);
            const totalReductionPercentage = initialRisk > 0 ? (initialRisk - residualRisk) / initialRisk : 0;

            return {
                stakeholder,
                initialRisk,
                residualRisk,
                totalReduction: totalReductionPercentage,
                relatedPaths,
                relatedMeasures,
                residualParams: {
                    dependency: currentD,
                    penetration: currentP,
                    cyberMaturity: currentM,
                    trust: currentC
                }
            };
        });
    };

    const rows = buildStakeholderRows();

    const initialStakeholdersForRadar = rows.map(r => ({
        ...r.stakeholder,
        threatLevel: r.initialRisk
    }));

    const residualStakeholdersForRadar = rows.map(r => ({
        ...r.stakeholder,
        dependency: r.residualParams.dependency,
        penetration: r.residualParams.penetration,
        cyberMaturity: r.residualParams.cyberMaturity,
        trust: r.residualParams.trust,
        threatLevel: r.residualRisk,
        initialValues: {
            dependency: r.stakeholder.dependency || 1,
            penetration: r.stakeholder.penetration || 1,
            cyberMaturity: r.stakeholder.cyberMaturity || 1,
            trust: r.stakeholder.trust || 1,
            threatLevel: r.initialRisk
        }
    }));

    // ---------------------------------------------------------------------------
    // Toggle expand
    // ---------------------------------------------------------------------------
    const toggleExpand = (id) => {
        setExpandedStakeholders(prev => ({ ...prev, [id]: !prev[id] }));
    };

    // ---------------------------------------------------------------------------
    // Residual risk expression handlers
    // ---------------------------------------------------------------------------
    const startEditingExpression = (stakeholderId) => {
        setEditingExpressions(prev => ({ ...prev, [stakeholderId]: true }));
    };

    const cancelEditingExpression = (stakeholderId) => {
        setEditingExpressions(prev => ({ ...prev, [stakeholderId]: false }));
    };

    const handleExpressionChange = (stakeholderId, value) => {
        setExpressionTexts(prev => ({ ...prev, [stakeholderId]: value }));
    };

    const saveExpression = async (stakeholderId) => {
        setSavingExpression(prev => ({ ...prev, [stakeholderId]: true }));
        try {
            const updatedStakeholders = stakeholders.map(s => {
                if (s.id === stakeholderId) {
                    return { ...s, residualRiskExpression: expressionTexts[stakeholderId] || '' };
                }
                return s;
            });
            const resp = await stakeholderService.saveStakeholders(analysisId, updatedStakeholders);
            if (resp.success) {
                setStakeholders(updatedStakeholders);
                setEditingExpressions(prev => ({ ...prev, [stakeholderId]: false }));
            }
        } catch (err) {
            console.error('[StakeholderRiskView] Error saving expression:', err);
        } finally {
            setSavingExpression(prev => ({ ...prev, [stakeholderId]: false }));
        }
    };

    const generateExpression = async (stakeholderId) => {
        if (aiGenerating[stakeholderId]) return;
        const row = rows.find(r => r.stakeholder.id === stakeholderId);
        if (!row) return;
        const { stakeholder, initialRisk, residualRisk, totalReduction, relatedPaths, relatedMeasures, residualParams } = row;
        setAiGenerating(prev => ({ ...prev, [stakeholderId]: true }));
        try {
            const round2 = (v) => Math.round(Number(v || 0) * 100) / 100;
            const payload = {
                analysisId,
                language: i18n.language?.startsWith('en') ? 'en' : 'fr',
                stakeholder: {
                    name: stakeholder.name,
                    initialRisk: round2(initialRisk),
                    residualRisk: round2(residualRisk),
                    initialParams: {
                        dependency: stakeholder.dependency || 1,
                        penetration: stakeholder.penetration || 1,
                        cyberMaturity: stakeholder.cyberMaturity || 1,
                        trust: stakeholder.trust || 1
                    },
                    residualParams: {
                        dependency: round2(residualParams.dependency),
                        penetration: round2(residualParams.penetration),
                        cyberMaturity: round2(residualParams.cyberMaturity),
                        trust: round2(residualParams.trust)
                    },
                    initialZone: getRiskZone(initialRisk, thresholds).label,
                    residualZone: getRiskZone(residualRisk, thresholds).label,
                    totalReductionPct: Math.round(totalReduction * 100),
                    relatedPaths: relatedPaths.slice(0, 10).map(p => ({
                        referenceCode: p.referenceCode,
                        sourceRisk: p.sourceRiskDescription || p.sourceRiskName,
                        objectif: p.objectifVise,
                        dreadedEvent: p.dreadedEventName
                    })),
                    relatedMeasures: relatedMeasures.slice(0, 20).map(m => ({
                        name: m.title || m.name,
                        implementation: m.implementation,
                        effectiveness: {
                            d: m.dependanceEffectiveness || 0,
                            p: m.penetrationEffectiveness || 0,
                            m: m.maturiteCyberEffectiveness || 0,
                            c: m.confianceEffectiveness || 0
                        },
                        description: String(m.description || '').slice(0, 300)
                    })),
                    existingExpression: expressionTexts[stakeholderId] || ''
                }
            };
            const resp = await api.post('/ai/suggest-stakeholder-residual-expression', payload);
            if (resp?.success && resp?.data?.expression) {
                setExpressionTexts(prev => ({ ...prev, [stakeholderId]: resp.data.expression }));
                startEditingExpression(stakeholderId);
            }
        } catch (err) {
            console.error('[StakeholderRiskView] Error generating expression:', err);
        } finally {
            setAiGenerating(prev => ({ ...prev, [stakeholderId]: false }));
        }
    };

    // ---------------------------------------------------------------------------
    // Render states
    // ---------------------------------------------------------------------------
    if (loading) {
        return (
            <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
                <span className="ml-3 text-gray-500">Chargement des données de risque...</span>
            </div>
        );
    }

    if (rows.length === 0) {
        return (
            <div className="text-center py-16 px-4">
                <div className="bg-blue-50 inline-flex p-4 rounded-full mb-4">
                    <Users size={32} className="text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Aucune partie prenante retenue</h3>
                <p className="text-gray-500 text-sm max-w-md mx-auto">
                    Ajoutez et retenez des parties prenantes dans l'Activité 1 de l'Atelier 3 pour visualiser les risques ici.
                </p>
            </div>
        );
    }

    // ---------------------------------------------------------------------------
    // Main render
    // ---------------------------------------------------------------------------
    return (
        <div className="space-y-4">

            {/* Radars Visualization Dashboard */}
            {thresholds && (
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <BarChart2 className="text-blue-600" size={20} />
                            <div>
                                <h3 className="text-base font-semibold text-gray-900">Cartographie Comparative des Menaces</h3>
                                <p className="text-xs text-gray-500">Visualisation de l'impact des mesures de sécurité sur la gravité des parties prenantes</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs font-medium">
                                <button
                                    onClick={() => setRiskFilter('retained')}
                                    className={`px-3 py-1.5 transition-colors ${riskFilter === 'retained' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                >
                                    Retenues
                                </button>
                                <button
                                    onClick={() => setRiskFilter('all')}
                                    className={`px-3 py-1.5 border-l border-gray-300 transition-colors ${riskFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                >
                                    Toutes
                                </button>
                            </div>
                            <button
                                onClick={() => setIsThresholdEditing(!isThresholdEditing)}
                                className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 flex items-center text-xs font-medium text-gray-700 transition-colors"
                            >
                                <Settings size={14} className="text-gray-500 mr-1.5" />
                                Modifier les seuils
                            </button>
                            <button
                                onClick={() => setShowRadars(!showRadars)}
                                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                {showRadars ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                            </button>
                        </div>
                    </div>

                    {showRadars && (
                        <div className="p-6 bg-slate-50/50">
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                                {/* Initial Radar Card */}
                                <div className="flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                                        <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                                            <span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>
                                            Radar de Menace Initial
                                        </h4>
                                        <span className="text-xs text-slate-500 px-2 py-0.5 bg-slate-100 rounded border border-slate-200">
                                            Avant mesures
                                        </span>
                                    </div>
                                    <div className="p-2 h-[480px]">
                                        <ThreatRadarVisualization
                                            ref={initialRadarRef}
                                            stakeholders={initialStakeholdersForRadar}
                                            thresholds={thresholds}
                                            onUpdateThresholds={handleUpdateThresholds}
                                            isLoading={false}
                                            isThresholdEditing={isThresholdEditing}
                                            setIsThresholdEditing={setIsThresholdEditing}
                                        />
                                    </div>
                                </div>

                                {/* Residual Radar Card */}
                                <div className="flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                                        <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                                            <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
                                            Radar de Menace Résiduel
                                        </h4>
                                        <span className="text-xs text-green-700 px-2 py-0.5 bg-green-50 rounded border border-green-200">
                                            Après mesures appliquées
                                        </span>
                                    </div>
                                    <div className="p-2 h-[480px]">
                                        <ThreatRadarVisualization
                                            ref={residualRadarRef}
                                            stakeholders={residualStakeholdersForRadar}
                                            thresholds={thresholds}
                                            onUpdateThresholds={handleUpdateThresholds}
                                            isLoading={false}
                                            isThresholdEditing={isThresholdEditing}
                                            setIsThresholdEditing={setIsThresholdEditing}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Legend */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
                <Info size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800 space-y-1">
                    <p className="font-semibold">Comment lire ce tableau ?</p>
                    <p>
                        Le <strong>risque initial</strong> correspond au niveau de menace calculé pour chaque partie prenante en Activité 1
                        (formule : <em>Dépendance × Pénétration / Maturité Cyber × Confiance</em>).
                    </p>
                    <p>
                        Le <strong>risque résiduel</strong> est obtenu en soustrayant (ou additionnant) l'efficacité des mesures aux paramètres de la partie prenante :
                        les valeurs de <em>Dépendance</em> et <em>Pénétration</em> diminuent, tandis que la <em>Maturité Cyber</em> et la <em>Confiance</em> augmentent.
                        Les résultats sont bornés entre 1 et 4 pour chaque paramètre avant de recalculer le risque final.
                    </p>
                    <p className="pt-1 font-medium">Zones :</p>
                    <div className="flex flex-wrap gap-2 pt-0">
                        {thresholds && [
                            { label: 'Zone de danger', cls: 'bg-red-100 text-red-800 border border-red-300', val: `≥ ${thresholds.danger.toFixed(1)}` },
                            { label: 'Zone de contrôle', cls: 'bg-orange-100 text-orange-800 border border-orange-300', val: `≥ ${thresholds.control.toFixed(1)}` },
                            { label: 'Zone de veille', cls: 'bg-teal-100 text-teal-800 border border-teal-300', val: `≥ ${thresholds.watch.toFixed(1)}` },
                            ...(thresholds.watch > 0 ? [{ label: 'Hors périmètre', cls: 'bg-slate-100 text-slate-700 border border-slate-300', val: `< ${thresholds.watch.toFixed(1)}` }] : []),
                        ].map(z => (
                            <span key={z.label} className={`px-2 py-0.5 rounded-full text-xs font-medium ${z.cls}`}>
                                {z.label} ({z.val})
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {/* Stakeholder rows */}
            {rows.map(({ stakeholder, initialRisk, residualRisk, totalReduction, relatedPaths, relatedMeasures }) => {
                const initialZone = getRiskZone(initialRisk, thresholds);
                const residualZone = getRiskZone(residualRisk, thresholds);
                const isExpanded = expandedStakeholders[stakeholder.id];
                const noMeasures = relatedMeasures.length === 0;
                const noPaths = relatedPaths.length === 0;

                return (
                    <div key={stakeholder.id} className="border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white">

                        {/* ---- Stakeholder header summary row ---- */}
                        <button
                            type="button"
                            onClick={() => toggleExpand(stakeholder.id)}
                            className="w-full text-left"
                        >
                            <div className="grid grid-cols-12 items-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors">

                                {/* Name + category */}
                                <div className="col-span-3 flex items-center gap-2">
                                    <div className={`w-2 h-2 rounded-full ${initialZone.dot} flex-shrink-0`}></div>
                                    <div>
                                        <p className="text-sm font-semibold text-gray-900 leading-tight">{stakeholder.name}</p>
                                        <p className="text-xs text-gray-500 capitalize">{stakeholder.category}</p>
                                    </div>
                                </div>

                                {/* Initial risk */}
                                <div className="col-span-2 text-center">
                                    <p className="text-xs text-gray-500 mb-1">Risque initial</p>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${initialZone.badge}`}>
                                        {initialRisk.toFixed(2)}
                                    </span>
                                    <p className="text-[10px] text-gray-400 mt-0.5">{initialZone.label}</p>
                                </div>

                                {/* Arrow */}
                                <div className="col-span-1 flex justify-center">
                                    <ArrowRight size={18} className="text-gray-400" />
                                </div>

                                {/* Residual risk */}
                                <div className="col-span-2 text-center">
                                    <p className="text-xs text-gray-500 mb-1">Risque résiduel</p>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${residualZone.badge}`}>
                                        {residualRisk.toFixed(2)}
                                    </span>
                                    <p className="text-[10px] text-gray-400 mt-0.5">{residualZone.label}</p>
                                </div>

                                {/* Total reduction */}
                                <div className="col-span-2 text-center">
                                    <p className="text-xs text-gray-500 mb-1">Baisse de risque</p>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${totalReduction > 0 ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-gray-100 text-gray-600 border border-gray-200'}`}>
                                        <TrendingDown size={11} className="mr-1" />
                                        -{Math.round(totalReduction * 100)}%
                                    </span>
                                    <p className="text-[10px] text-gray-400 mt-0.5">{relatedMeasures.length} mesure{relatedMeasures.length !== 1 ? 's' : ''}</p>
                                </div>

                                {/* Expand/Collapse toggle */}
                                <div className="col-span-2 flex items-center justify-end gap-1 text-gray-400 text-xs">
                                    <span>{noPaths ? 'Aucun chemin' : `${relatedPaths.length} chemin${relatedPaths.length !== 1 ? 's' : ''}`}</span>
                                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                </div>
                            </div>
                        </button>

                        {/* ---- Expanded detail: per-measure rows ---- */}
                        {isExpanded && (
                            <div className="border-t border-gray-200">

                                {/* Attack paths involved */}
                                {relatedPaths.length > 0 && (
                                    <div className="px-4 py-2 bg-slate-50 border-b border-gray-100 flex flex-wrap gap-2 items-center">
                                        <span className="text-xs font-medium text-gray-500">Chemins d'attaque :</span>
                                        {relatedPaths.map(path => (
                                            <span key={path.id} className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold bg-blue-100 text-blue-800 border border-blue-200">
                                                {path.strategicScenarioReference ? `${path.strategicScenarioReference} › ` : ''}
                                                {path.referenceCode}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {/* Measures table */}
                                {noMeasures ? (
                                    <div className="px-4 py-6 text-center">
                                        <Shield size={24} className="text-gray-300 mx-auto mb-2" />
                                        <p className="text-sm text-gray-500">Aucune mesure de sécurité n'est appliquée aux chemins d'attaque de cette partie prenante.</p>
                                        {noPaths && (
                                            <p className="text-xs text-gray-400 mt-1">Cette partie prenante n'est associée à aucun chemin d'attaque défini.</p>
                                        )}
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-100">
                                            <thead>
                                                <tr className="bg-gray-50">
                                                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-1/3">Mesure de sécurité</th>
                                                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Statut</th>
                                                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Efficacité</th>
                                                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Réduction</th>
                                                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Risque résiduel après</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {relatedMeasures.map((measure, idx) => {
                                                    const eff = getAppliedEffectiveness(measure);
                                                    const hasEffectiveness = eff.d > 0 || eff.p > 0 || eff.m > 0 || eff.c > 0;
                                                    
                                                    // Calculate residual AFTER this sequence of measures
                                                    const measuresUpToHere = relatedMeasures.slice(0, idx + 1);
                                                    
                                                    let tempD = stakeholder.dependency || 1;
                                                    let tempP = stakeholder.penetration || 1;
                                                    let tempM = stakeholder.cyberMaturity || 1;
                                                    let tempC = stakeholder.trust || 1;

                                                    measuresUpToHere.forEach(m => {
                                                        const mEff = getAppliedEffectiveness(m);
                                                        tempD -= mEff.d;
                                                        tempP -= mEff.p;
                                                        tempM += mEff.m;
                                                        tempC += mEff.c;
                                                    });

                                                    tempD = Math.max(1, Math.min(4, tempD));
                                                    tempP = Math.max(1, Math.min(4, tempP));
                                                    tempM = Math.max(1, Math.min(4, tempM));
                                                    tempC = Math.max(1, Math.min(4, tempC));

                                                    const residualAfterThisMeasure = (tempD * tempP) / (tempM * tempC);
                                                    const residualAfterZone = getRiskZone(residualAfterThisMeasure, thresholds);

                                                    return (
                                                        <tr key={measure.id || idx} className="hover:bg-gray-50 transition-colors">
                                                            <td className="px-4 py-3">
                                                                <p className="text-sm font-medium text-gray-900">{measure.title}</p>
                                                                <p className="text-xs text-gray-400 capitalize">{measure.category}</p>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getImplementationBadge(measure.implementation)}`}>
                                                                    {getImplementationLabel(measure.implementation)}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                {renderEffectivenessTags(measure)}
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <span className={`text-sm font-bold ${hasEffectiveness ? 'text-green-700' : 'text-gray-400'}`}>
                                                                    {formatEffectivenessText(eff)}
                                                                </span>
                                                                {!hasEffectiveness && (
                                                                    <p className="text-[10px] text-gray-400">({getImplementationLabel(measure.implementation)})</p>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${residualAfterZone.badge}`}>
                                                                    {residualAfterThisMeasure.toFixed(2)}
                                                                </span>
                                                                <p className="text-[10px] text-gray-400 mt-0.5">{residualAfterZone.label}</p>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>

                                            {/* Footer: final residual */}
                                            <tfoot className="bg-green-50 border-t-2 border-green-200">
                                                <tr>
                                                    <td colSpan={3} className="px-4 py-2 text-xs font-semibold text-gray-600">
                                                        Risque résiduel final ({relatedMeasures.length} mesure{relatedMeasures.length !== 1 ? 's' : ''} appliquée{relatedMeasures.length !== 1 ? 's' : ''})
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        <span className="text-sm font-bold text-green-700">-{Math.round(totalReduction * 100)}%</span>
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-sm font-bold ${residualZone.badge}`}>
                                                            {residualRisk.toFixed(2)}
                                                        </span>
                                                        <p className="text-[10px] text-gray-500 mt-0.5">{residualZone.label}</p>
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                )}

                                {/* ---- Residual risk expression ---- */}
                                <div className="border-t border-gray-200 px-4 py-3 bg-white">
                                    <div className="flex items-start gap-3">
                                        <FileText size={16} className="text-gray-400 mt-0.5" />
                                        <div className="flex-1">
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Expression du risque résiduel
                                            </label>
                                            {editingExpressions[stakeholder.id] ? (
                                                <>
                                                    <textarea
                                                        value={expressionTexts[stakeholder.id] || ''}
                                                        onChange={(e) => handleExpressionChange(stakeholder.id, e.target.value)}
                                                        rows={2}
                                                        placeholder="Décrivez l'expression du risque résiduel pour cette partie prenante..."
                                                        className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 focus:ring-2 transition-all duration-150 text-sm"
                                                    />
                                                    <div className="flex justify-end gap-2 mt-2">
                                                        {companyAiEnabled && (
                                                            <AiAssistantButton
                                                                onClick={() => generateExpression(stakeholder.id)}
                                                                isLoading={!!aiGenerating[stakeholder.id]}
                                                                title={t('workshop3.activity4.residualExpressionAiTooltip', 'Générer avec l’Assistant ACG')}
                                                            />
                                                        )}
                                                        <button
                                                            onClick={() => cancelEditingExpression(stakeholder.id)}
                                                            className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors"
                                                        >
                                                            Annuler
                                                        </button>
                                                        <button
                                                            onClick={() => saveExpression(stakeholder.id)}
                                                            disabled={savingExpression[stakeholder.id]}
                                                            className="px-4 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                        >
                                                            {savingExpression[stakeholder.id] ? 'Enregistrement...' : 'Enregistrer'}
                                                        </button>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="flex items-start justify-between gap-3">
                                                    <p className={`text-sm ${expressionTexts[stakeholder.id] ? 'text-gray-800' : 'text-gray-400 italic'}`}>
                                                        {expressionTexts[stakeholder.id] || 'Aucune expression saisie.'}
                                                    </p>
                                                    <div className="flex items-center gap-2 flex-shrink-0">
                                                        {companyAiEnabled && (
                                                            <AiAssistantButton
                                                                onClick={() => generateExpression(stakeholder.id)}
                                                                isLoading={!!aiGenerating[stakeholder.id]}
                                                                title={t('workshop3.activity4.residualExpressionAiTooltip', 'Générer avec l’Assistant ACG')}
                                                            />
                                                        )}
                                                        <button
                                                            onClick={() => startEditingExpression(stakeholder.id)}
                                                            className="px-3 py-1 bg-white border border-gray-300 text-gray-600 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors"
                                                        >
                                                            Modifier
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default StakeholderRiskView;
